import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { AutosaveIndicator } from '../components/form/AutosaveIndicator';
import { DRAFTS_IN_CALENDAR_PATH } from '../components/DraftsButton';
import { DiscardDraftButton } from '../components/form/DiscardDraftButton';
import { ProjectFormDocument } from '../components/form/ProjectFormDocument';
import type { SubmitState } from '../components/form/ReviewSheet';
import { useProjectAutosave } from '../hooks/useProjectAutosave';
import {
  useMinistryMembers,
  useProjectCatalogs,
  useWritableMinistries,
} from '../hooks/useProjectCatalogs';
import { useDiscardDraft, useSubmitProject } from '../hooks/useProjectMutations';
import { useProject, useProjectSupportItems } from '../hooks/useProjects';
import { useProjectTimeline } from '../hooks/useProjectTimeline';
import { lastChangeRequest } from '../lib/timeline';
import { emptyProjectForm, type ProjectFormValues } from '../schemas/project.schema';
import type { ProjectDraftPayload } from '../api/projects.api';
import { Breadcrumb } from '@/shared/components/Breadcrumb';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingState } from '@/shared/components/LoadingState';
import { PageHeader } from '@/shared/components/PageHeader';
import { Button } from '@/shared/components/ui/button';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';
import type { Id } from '@/shared/types/domain';

/**
 * Uma tela para `/calendario/novo` e `/calendario/:id/editar` (spec §7.1).
 *
 * ⚠️ **O endereço mudou: o formulário é do Calendário**, e os dois de
 * `/projetos` redirecionam para cá (o porquê está em `LegacyFormRedirect`, em
 * `router.tsx`). O que isso governa DENTRO desta tela é a saída: a trilha e o
 * "voltar" apontam para `/calendario` — a única aba que os cinco papéis têm —,
 * e não para uma listagem que, para `leader` e `assistant`, não está no menu.
 *
 * **O rascunho só nasce no primeiro campo de CONTEÚDO preenchido**, não ao
 * abrir e não ao escolher o ministério: criar ao abrir encheria a tabela de
 * projeto vazio a cada clique curioso em "Novo projeto", e a métrica de
 * abandono (PRD §6.3) passaria a medir curiosidade em vez de desistência.
 * Quando nasce, a rota vira `/calendario/:id/editar` por `replace` — sem
 * empilhar histórico, para que o botão voltar não devolva a pessoa a um
 * formulário em branco.
 *
 * ⚠️ **O ministério deixou de ser o gatilho da criação, e isso é o conserto de
 * um nó.** Escolher o ministério era o gesto que criava o rascunho, e criar o
 * rascunho era o gesto que TRANCAVA a escolha (`ministryLocked`): quem clicava
 * no ministério errado no primeiro campo do documento ficava com um projeto
 * cujo dono não dava para desfazer — e com uma linha no banco que ninguém
 * pediu, porque a pessoa ia embora sem escrever nada. Agora são duas coisas
 * separadas: **o ministério sozinho não cria** (é escopo, não conteúdo), e
 * **enquanto for rascunho ele continua editável**, pela migration
 * `draft_ministry_is_editable`. Fora do rascunho o veto do banco é o de sempre.
 *
 * **"Descartar rascunho" aparece desde o primeiro frame**, antes de existir
 * projeto: para quem está na tela, a saída não pode depender de um estado de
 * servidor que ela não vê. Sem projeto ele é só a porta de saída — e é a
 * porta que garante que nada foi gravado.
 */
export function ProjectFormPage() {
  const { id: routeProjectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [projectId, setProjectId] = useState<Id | null>(routeProjectId ?? null);
  const [values, setValues] = useState<ProjectFormValues>(emptyProjectForm);
  const [isHydrated, setIsHydrated] = useState(!routeProjectId);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [discardError, setDiscardError] = useState<unknown>(null);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  const catalogs = useProjectCatalogs();
  const ministries = useWritableMinistries();
  const project = useProject(projectId ?? undefined);
  const supportItems = useProjectSupportItems(routeProjectId);
  const members = useMinistryMembers(values.ministry_id);

  const submitMutation = useSubmitProject();
  const discardMutation = useDiscardDraft();

  const autosave = useProjectAutosave({
    projectId,
    version: project.data?.version ?? null,
    onCreated: (createdId) => {
      setProjectId(createdId);
      navigate(`/calendario/${createdId}/editar`, { replace: true });
    },
    enabled: isHydrated,
  });

  /**
   * Preenche o formulário com o que está no servidor — uma vez só.
   *
   * O `hydratedRef` existe porque cada save devolve o projeto atualizado, e
   * re-hidratar a cada resposta sobrescreveria o que a pessoa digitou nos 2s
   * seguintes ao último save. O servidor é a verdade na ABERTURA; depois dela,
   * quem manda é quem está digitando (e o conflito de versão é quem protege o
   * caso dos dois aparelhos).
   */
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !routeProjectId) return;
    if (!project.data || supportItems.isPending || !catalogs.data) return;

    hydratedRef.current = true;
    const spaceIds = catalogs.data.spaces
      .filter((space) => project.data?.space_names.includes(space.name))
      .map((space) => space.id);

    setValues({
      ministry_id: project.data.ministry_id,
      title: project.data.title,
      description: project.data.description,
      category_id: project.data.category_id,
      starts_at: project.data.starts_at,
      ends_at: project.data.ends_at,
      expected_audience: project.data.expected_audience,
      responsible_profile_id: project.data.responsible_profile_id,
      estimated_budget_cents: project.data.estimated_budget_cents,
      funding_source_id: project.data.funding_source_id,
      budget_notes: project.data.budget_notes,
      extra_info: project.data.extra_info,
      space_ids: spaceIds,
      support_items: (supportItems.data ?? []).flatMap((item) => {
        const type = catalogs.data?.supportTypes.find(
          (entry) => entry.name === item.support_type_name,
        );
        return type
          ? [{ support_type_id: type.id, request: item.request, needed_by: item.needed_by }]
          : [];
      }),
    });
    setIsHydrated(true);
  }, [routeProjectId, project.data, supportItems.isPending, supportItems.data, catalogs.data]);

  /**
   * O dia que veio do menu de contexto do Calendário (spec-calendar.md decisão
   * 75): botão direito num dia → "Novo projeto neste dia".
   *
   * Só vale em projeto NOVO: abrir um rascunho existente com `?data=` na URL
   * não pode mexer no que a pessoa já escreveu. E é validado pelo formato antes
   * de virar valor de campo — parâmetro escrito à mão que não faz sentido é
   * ignorado em silêncio, nunca exibido como erro.
   */
  const dateFromUrl = searchParams.get('data');
  const suggestedDate =
    !routeProjectId && dateFromUrl && /^\d{4}-\d{2}-\d{2}$/.test(dateFromUrl)
      ? dateFromUrl
      : undefined;

  /** Ministério pré-selecionado quando o "Novo projeto" veio da tela de um. */
  const ministryFromUrl = searchParams.get('ministerio');
  useEffect(() => {
    if (routeProjectId || !ministryFromUrl || !ministries.data) return;
    // Ministério fora do alcance de escrita é IGNORADO em silêncio: a
    // autorização real é a RPC, e um erro aqui só ensinaria a sondar ids.
    const allowed = ministries.data.some((m) => m.ministry_id === ministryFromUrl);
    if (!allowed) return;
    setValues((current) =>
      current.ministry_id ? current : { ...current, ministry_id: ministryFromUrl },
    );
  }, [routeProjectId, ministryFromUrl, ministries.data]);

  /** Um único vínculo de escrita: o ministério é escolhido sozinho (US-08). */
  useEffect(() => {
    if (routeProjectId || !ministries.data || ministries.data.length !== 1) return;
    setValues((current) =>
      current.ministry_id
        ? current
        : { ...current, ministry_id: ministries.data[0]!.ministry_id },
    );
  }, [routeProjectId, ministries.data]);

  /**
   * O patch que vai para a RPC carrega SÓ o que mudou — depois que o projeto
   * existe.
   *
   * A RPC decide o que gravar pela PRESENÇA da chave (`p_payload ? 'title'`),
   * então mandar o formulário inteiro a cada tecla reescreveria campos que
   * ninguém tocou — e, num projeto aprovado, cada um deles viraria linha na
   * linha do tempo.
   *
   * **A criação é a exceção, e por dois motivos.** Ela exige `ministry_id`,
   * que muitas vezes nunca passou por um patch (com um vínculo só, ele é
   * escolhido sozinho — US-08); e o que a pessoa digitou ANTES de escolher o
   * ministério precisa entrar junto, senão o primeiro parágrafo do objetivo
   * se perderia no exato momento em que o projeto nasce. Por isso o primeiro
   * save manda o formulário inteiro.
   */
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const applyPatch = useCallback(
    (patch: Partial<ProjectFormValues>, immediate: boolean) => {
      const next = { ...valuesRef.current, ...patch };
      valuesRef.current = next;
      setValues(next);

      const isCreation = projectId === null;
      // Sem ministério não há o que criar: a RPC recusaria com
      // `ministry_required`, e o erro apareceria enquanto a pessoa ainda está
      // escolhendo. E sem CONTEÚDO não há o que salvar: o ministério sozinho
      // é a resposta a "em nome de quem", que a tela já mostra sem gravar
      // nada. O que a pessoa escreveu antes de qualquer um dos dois fica no
      // formulário e sobe junto no save que finalmente criar o projeto.
      if (isCreation && (!next.ministry_id || !hasContent(next))) return;

      const payload = (isCreation ? next : patch) as ProjectDraftPayload;
      if (immediate) autosave.saveNow(payload);
      else autosave.save(payload);
    },
    [autosave, projectId],
  );

  const onChangeText = useCallback(
    (patch: Partial<ProjectFormValues>) => applyPatch(patch, false),
    [applyPatch],
  );

  /**
   * Campo discreto — e o ministério é o único que arrasta outros dois consigo.
   *
   * Responsável e espaço são RESPOSTAS ao ministério, não campos independentes:
   * o primeiro precisa de vínculo vigente nele, o segundo é sala da filial
   * dele. Trocar o ministério e deixar os dois na tela mostraria um nome que a
   * lista de responsáveis não contém mais e um espaço que a RPC recusaria no
   * envio — os dois com aparência de preenchidos.
   *
   * A mesma limpeza acontece no banco (`draft_ministry_is_editable`), e a
   * repetição é de propósito: aqui é para a tela não mentir, lá é para o dado
   * não ficar torto se a limpeza daqui não chegar.
   */
  const onChangeNow = useCallback(
    (patch: Partial<ProjectFormValues>) => {
      // TROCAR limpa; escolher pela primeira vez, não. Quem preencheu a seção
      // 3 antes da 1 — fora de ordem, mas possível — não pode perder os
      // espaços que marcou só por finalmente dizer de quem é o projeto.
      const isMinistryChange =
        patch.ministry_id !== undefined &&
        valuesRef.current.ministry_id !== null &&
        patch.ministry_id !== valuesRef.current.ministry_id;

      applyPatch(
        isMinistryChange
          ? { ...patch, responsible_profile_id: null, space_ids: [] }
          : patch,
        true,
      );
    },
    [applyPatch],
  );

  const isEditing = Boolean(routeProjectId);
  const status = project.data?.status;
  const canEdit = !status || status === 'draft' || status === 'changes_requested' || status === 'approved';

  /**
   * **O modo ajuste** (spec-project-adjustments.md §4): a mesma tela, outra
   * moldura. Governado por `status`, e só — nada aqui toca o caminho do projeto
   * novo, que é o que o critério de aceite 10 vigia.
   */
  const isAdjusting = status === 'changes_requested';

  /**
   * O pedido do revisor (decisão 45). **Mesma query key da tela de detalhe** —
   * `queryKeys.projects.timeline(id)` —, então o TanStack resolve as duas telas
   * com uma requisição: quem chega aqui pelo botão "Ajustar projeto" já traz a
   * linha do tempo no cache. Só dispara no modo ajuste; o projeto novo não tem
   * história para consultar.
   */
  const timeline = useProjectTimeline(isAdjusting ? (projectId ?? undefined) : undefined);
  const reviewerRequest = lastChangeRequest(timeline.data ?? []);

  /**
   * O intervalo entre "enviado" e a tela do projeto.
   *
   * Ele existe porque a confirmação precisa ser VISTA. Navegar no mesmo frame
   * em que a RPC responde faz a pessoa chegar numa tela nova sem ter registrado
   * que a ação deu certo — e quem não registra, volta e envia de novo. Um
   * segundo é o tempo do visto crescer e do olho ler "Projeto enviado";
   * segurar mais do que isso vira espera.
   */
  const sentTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (sentTimerRef.current) window.clearTimeout(sentTimerRef.current);
    },
    [],
  );

  async function handleSubmit(comment: string | null) {
    if (submitState !== 'idle') return;
    setSubmitError(null);
    setSubmitState('submitting');
    try {
      // Tudo que estiver em debounce precisa chegar ANTES da submissão: sem
      // isso, a última frase digitada ficaria de fora do que o revisor lê.
      await autosave.flush();
      if (!projectId || !values.ministry_id) {
        setSubmitState('idle');
        return;
      }

      await submitMutation.mutateAsync({
        projectId,
        ministryId: values.ministry_id,
        // `submitProject` já apara e converte string em branco em `undefined`
        // — a aresta do reenvio aceita observação vazia (decisão 16).
        comment,
      });

      setSubmitState('sent');
      sentTimerRef.current = window.setTimeout(() => {
        // A folha do projeto continua em `/projetos/:id` (não há
        // `/calendario/:id`), mas quem enviou veio da grade: `from` mantém o
        // Calendário aceso e faz a trilha oferecer a volta para lá.
        navigate(`/projetos/${projectId}`, { state: { from: '/calendario' } });
      }, 1100);
    } catch (error) {
      setSubmitError(error);
      setSubmitState('idle');
    }
  }

  /**
   * Descartar — e as duas metades dele.
   *
   * **`cancel()` vem primeiro, sempre.** Sem ele, a frase que estava em
   * debounce sairia depois da navegação e recriaria o que a pessoa acabou de
   * apagar (ou criaria, se o projeto ainda nem existia). É o único lugar do
   * formulário onde perder o que está pendente é o comportamento certo.
   *
   * **Sem projeto no servidor, descartar é só sair.** Nada foi gravado, então
   * não há RPC a chamar — e é isso que faz o botão poder existir desde o
   * primeiro frame sem prometer uma ação que não tem sujeito.
   */
  async function handleDiscard() {
    setDiscardError(null);
    autosave.cancel();

    if (!projectId || !values.ministry_id) {
      navigate(DRAFTS_IN_CALENDAR_PATH);
      return;
    }

    try {
      await discardMutation.mutateAsync({ projectId, ministryId: values.ministry_id });
      navigate(DRAFTS_IN_CALENDAR_PATH);
    } catch (error) {
      setDiscardError(error);
    }
  }

  if (isEditing && project.isPending) {
    return <LoadingState label="Carregando o projeto…" />;
  }

  if (isEditing && !project.data) {
    return (
      <EmptyState
        title="Projeto não encontrado"
        description={messages.errors.notFound}
        action={<Button onClick={() => navigate('/calendario')}>Ver o calendário</Button>}
      />
    );
  }

  if (isEditing && !canEdit) {
    return (
      <EmptyState
        title="Este projeto não pode ser editado agora"
        description={
          'Ele está em análise. Assim que houver uma decisão, ele volta a ficar editável — ou você recebe o pedido de ajustes.'
        }
        action={
          <Button
            onClick={() =>
              navigate(`/projetos/${routeProjectId}`, { state: { from: '/calendario' } })
            }
          >
            Ver o projeto
          </Button>
        }
      />
    );
  }

  if (catalogs.isPending || ministries.isPending) {
    return <LoadingState label="Carregando o formulário…" />;
  }

  if (catalogs.error || ministries.error) {
    return (
      <EmptyState
        title="Não foi possível carregar o formulário"
        description={toUserMessage(catalogs.error ?? ministries.error)}
      />
    );
  }

  if ((ministries.data?.length ?? 0) === 0) {
    return (
      <EmptyState
        title="Você não tem ministério para criar projeto"
        description="Criar projeto depende de um vínculo de liderança vigente. Fale com a secretaria da igreja."
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={
          <Breadcrumb
            items={[
              // A aba dona desta rota, e é fixa de propósito: o formulário só
              // tem um endereço agora, e é `/calendario/…`. Nada de
              // `originCrumb` aqui — a tela não muda de dono conforme a origem.
              { label: 'Calendário', to: '/calendario' },
              { label: isEditing ? (values.title ?? 'Rascunho') : messages.form.newProject },
            ]}
          />
        }
        title={values.title?.trim() || messages.form.newProject}
        description={isAdjusting ? messages.form.adjustHint : messages.form.newProjectHint}
        titleAside={
          <AutosaveIndicator
            state={autosave.state}
            isAdjusting={isAdjusting}
            onReload={() => window.location.reload()}
          />
        }
        /* Rascunho — inclusive o que ainda não existe no servidor. O que ele
           NÃO pode aparecer é no modo ajuste e no projeto aprovado: ali
           "descartar" apagaria um compromisso que outras pessoas já leram, e a
           RPC recusaria de qualquer forma. */
        actions={
          !isEditing || status === 'draft' ? (
            <DiscardDraftButton
              isDiscarding={discardMutation.isPending}
              error={discardError}
              onConfirm={() => void handleDiscard()}
            />
          ) : null
        }
      />

      <ProjectFormDocument
        values={values}
        ministries={ministries.data ?? []}
        members={members.data ?? []}
        spaces={catalogs.data?.spaces ?? []}
        categories={catalogs.data?.categories ?? []}
        supportTypes={catalogs.data?.supportTypes ?? []}
        fundingSources={catalogs.data?.fundingSources ?? []}
        projectId={projectId ?? undefined}
        /* Trancado só a partir do envio: enquanto for rascunho o banco aceita
           a troca (`draft_ministry_is_editable`), e um projeto novo ainda nem
           tem status. */
        ministryLocked={isEditing && status !== 'draft'}
        suggestedDate={suggestedDate}
        submitState={submitState}
        submitError={submitError}
        isAdjusting={isAdjusting}
        reviewerRequest={reviewerRequest}
        onChangeText={onChangeText}
        onChangeNow={onChangeNow}
        onSubmit={(comment) => void handleSubmit(comment)}
      />
    </div>
  );
}

/**
 * Tem alguma coisa aqui que valha uma linha no banco?
 *
 * `ministry_id` fica DE FORA da conta, e é o ponto todo: ele é o escopo do
 * projeto, não o projeto. Com um vínculo só ele é escolhido sozinho (US-08) —
 * ou seja, "abriu a tela" e "escolheu o ministério" são o mesmo evento para a
 * maioria dos líderes, e contá-lo como conteúdo criaria um rascunho por
 * abertura de formulário.
 *
 * A lista é derivada de `emptyProjectForm` em vez de escrita à mão: campo novo
 * no schema passa a contar sozinho, sem ninguém lembrar de vir aqui.
 */
function hasContent(values: ProjectFormValues): boolean {
  return (Object.keys(emptyProjectForm) as (keyof ProjectFormValues)[]).some((field) => {
    if (field === 'ministry_id') return false;
    const value = values[field];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return value !== null && value !== undefined;
  });
}
